import React, {
  useMemo,
  useRef,
  useEffect,
  memo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { size } from 'mathjs';

const DroppableGroup_takeoff = ({
  groupName,
  groupingKey,
  groupingKeyLabel = '',
  itemCount,
  isCollapsed,
  onToggleCollapse,
  children,
  selectedShapeIds,
  onShapeSelect,
  groupShapeIds, // NEW: explicit list of shape IDs in this group
  isAssemblyGrouping = false, // NEW: whether we're grouping by Assembly
  assemblyData = null, // NEW: assembly params data for table header
  onAddParam = null, // NEW: callback to add param to all shapes in group
  onApplyAssembly = null, // NEW: callback to load full assembly params onto all shapes in group
}) => {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: `group-${groupName}`,
    data: {
      type: 'group',
      groupName: groupName,
    },
  });

  const checkboxRef = useRef(null);

  // State for adding new parameters
  const [paramInputs, setParamInputs] = useState({
    key: '',
    value: '',
  });
  const [isAddingParam, setIsAddingParam] = useState(false);

  // keep last known child shape ids so checkbox still works when children are not rendered (collapsed)
  const lastChildShapeIdsRef = useRef([]);

  const childShapeIds = useMemo(() => {
    // Prefer explicit list when provided
    if (groupShapeIds && groupShapeIds.length) {
      lastChildShapeIdsRef.current = groupShapeIds;
      return groupShapeIds;
    }

    // Try to derive from children; if none (e.g. collapsed) fall back to last known list
    const idsFromChildren = React.Children.toArray(children).flatMap(
      (child) => child.props?.shapeIds || [],
    );

    if (idsFromChildren && idsFromChildren.length) {
      lastChildShapeIdsRef.current = idsFromChildren;
      return idsFromChildren;
    }

    return lastChildShapeIdsRef.current;
  }, [groupShapeIds, children]);

  const selectedChildrenCount = useMemo(
    () =>
      childShapeIds.filter((id) => selectedShapeIds.includes(id))
        .length,
    [childShapeIds, selectedShapeIds],
  );

  const isAllSelected =
    childShapeIds.length > 0 &&
    selectedChildrenCount === childShapeIds.length;
  const isPartiallySelected =
    selectedChildrenCount > 0 &&
    selectedChildrenCount < childShapeIds.length;

  useEffect(() => {
    if (checkboxRef.current) {
      // Keep indeterminate controlled, but checkbox checked is controlled below via prop
      checkboxRef.current.indeterminate = isPartiallySelected;
    }
  }, [isPartiallySelected]);

  const handleGroupSelect = (e) => {
    // Use the actual checked value from the checkbox event
    const shouldSelectAll = !!e.target.checked;
    onShapeSelect(childShapeIds, shouldSelectAll);
  };

  const handleParamAdd = () => {
    const { key, value } = paramInputs;
    if (!onAddParam || !key || !key.trim() || !value || !value.trim())
      return;

    // Call the parent callback with the new param key/value and group shape ids
    onAddParam(childShapeIds, key.trim(), value.trim());

    setParamInputs({ key: '', value: '' });
    setIsAddingParam(false);
  };

  const headerStyle = {
    cursor: 'pointer',
    padding: '8px',
    backgroundColor: isOver ? '#e0e0e0' : 'transparent', // Highlight when dragging over
    transition: 'background-color 0.2s ease',
  };

  // Get assembly param keys for table header (excluding internal keys)
  const assemblyParamKeys = useMemo(() => {
    if (!isAssemblyGrouping || !assemblyData?.params) return [];
    const params = assemblyData.params;
    const orderArray = Array.isArray(params.order)
      ? params.order
      : [];
    const allKeys = Object.keys(params).filter(
      (k) =>
        k !== 'order' &&
        k !== 'Assembly' &&
        k !== 'Total Length' &&
        k !== 'Total Area' &&
        k !== 'Groups',
    );
    // Use order array if present, otherwise use all keys
    return orderArray.length > 0
      ? orderArray.filter((k) => allKeys.includes(k))
      : allKeys;
  }, [isAssemblyGrouping, assemblyData]);

  return (
    // Make the whole group container the droppable node for a larger hit area
    <div
      className={`shape-group ${isAssemblyGrouping ? 'assembly-table' : ''}`}
      ref={setNodeRef}
      style={{ marginTop: '8px' }}
    >
      <div
        style={{ display: 'grid', gridTemplateColumns: '1.5rem 1fr' }}
      >
        <input
          ref={checkboxRef}
          type="checkbox"
          id={`group-checkbox-${groupingKey}-${groupName}`}
          onChange={handleGroupSelect}
          checked={isAllSelected}
          style={{ margin: 'auto' }}
        />
        {/* <span style={{margin: 'auto'}}></span> */}

        {/* Assembly table header row */}
        <div className="shape-list-header takeoff-row">
          <div
            onClick={onToggleCollapse}
            style={headerStyle}
            className="shape-info-2"
          >
            <span
              className="shape-type"
              style={{ fontSize: '0.85rem' }}
            >
              {isCollapsed ? '➕ ' : '➖ '}
              {isAssemblyGrouping
                ? (assemblyData?.name || groupName)
                : `${groupingKeyLabel}: ${groupName}`}{' '}
              ({itemCount})
            </span>
          </div>

          {/* <div className="shape-info">
                            <span className="shape-type">Type</span>
                            </div> */}
          {isAssemblyGrouping && assemblyParamKeys.length > 0 && (
            <>
              {!isCollapsed && (
                <div className="shape-page-info"></div>
              )}
              {!isCollapsed && (
                <div className="detail-content">
                  <div className="shape-params-container-takeoff">
                    {['Assembly', 'Reference'].map(
                      (internalKey) => (
                        <div
                          key={internalKey}
                          className={`shape-param-item shape-param-item-takeoff param-${internalKey.toLowerCase()}`}
                        >
                          <span className="shape-param-key">
                            {t(
                              `inventory.${internalKey.toLowerCase()}_label`,
                            )}
                          </span>
                        </div>
                      ),
                    )}
                    {assemblyParamKeys.map((key) => (
                      <div
                        key={key}
                        className="shape-param-item shape-param-item-takeoff"
                      >
                        <span className="shape-param-key">
                          {key}
                        </span>{' '}
                      </div>
                    ))}
                    <div className="shape-param-item shape-param-item-takeoff">
                      <span className="shape-param-key">
                        {isAddingParam ? (
                          <div className=".shape-param-item-takeoff">
                            <input
                              type="text"
                              className="shape-param-key"
                              placeholder={t(
                                'inventory.param_key_placeholder',
                              )}
                              value={paramInputs.key}
                              onChange={(e) =>
                                setParamInputs({
                                  ...paramInputs,
                                  key: e.target.value,
                                })
                              }
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: '80px',
                                height: '1rem',
                                fontSize: '0.75rem',
                                padding: '0.125rem 0.5rem',
                              }}
                            />
                            <input
                              type="text"
                              className="shape-param-value"
                              placeholder={t(
                                'inventory.param_value_placeholder',
                              )} // translated
                              value={paramInputs.value}
                              onChange={(e) =>
                                setParamInputs({
                                  ...paramInputs,
                                  value: e.target.value,
                                })
                              }
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  handleParamAdd();
                              }}
                              style={{
                                width: '80px',
                                height: '1rem',
                                fontSize: '0.75rem',
                                padding: '0.125rem 0.5rem',
                              }}
                            />
                            <button
                              className="outline"
                              style={{
                                height: '1.2rem',
                                padding: '0 4px',
                                fontSize: '0.75rem',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleParamAdd();
                              }}
                            >
                              {t('inventory.save')}
                            </button>
                            <button
                              className="outline"
                              style={{
                                height: '1.2rem',
                                padding: '0 4px',
                                fontSize: '0.65rem',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsAddingParam(false);
                                setParamInputs({
                                  key: '',
                                  value: '',
                                });
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <button
                            title={t('inventory.add_parameter')}
                            className="outline"
                            style={{
                              height: '1rem',
                              width: '3rem',
                              padding: '0',
                              fontSize: '0.65rem',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsAddingParam(true);
                            }}
                          >
                            +
                          </button>
                        )}
                        <button
                          title={t('inventory.add_apply_params')}
                          className="outline"
                          style={{
                            height: '1rem',
                            width: '3rem',
                            padding: '0',
                            fontSize: '0.65rem',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onApplyAssembly) onApplyAssembly(groupName, childShapeIds);
                          }}
                        >
                          📩
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {!isCollapsed && children}
    </div>
  );
};

export default memo(DroppableGroup_takeoff);
